import { createContext, useContext } from "react";
import axiosInstant from "../../api";
import { getBrandProjectFileMessages, orderShareFileMessages, publicFileMessages } from "../../api/endpoints";

/**
 * Set by the public share pages (project link, order link, order share page)
 * so components shared with the app can reach the token-scoped public routes
 * instead of logged-in ones — e.g. a video's comments for the comments
 * download. Null everywhere in the logged-in app.
 */
export interface PublicShare {
    /** The share link's token; unset on the order share page, which never gets it. */
    token?: string;
    /** Which share link the token belongs to. */
    base: "brand-projects" | "brand-orders";
    /** Order share page (/orders/share/:id): its routes go by order id. */
    orderId?: number;
    /** The project the share page is showing (cards don't carry it). */
    projectId?: number;
}

export const PublicShareContext = createContext<PublicShare | null>(null);

export const usePublicShare = () => useContext(PublicShareContext);

// One video's comments for the comments exports (CSV/PDF), through the share
// link on a share page. `?versions=all` asks for every version of the video
// (TDC-1733): the review thread shows one version, the exports cover them all
// with their Version labels. An API without the param returns the full
// thread anyway.
export const fetchFileComments = async (
    share: PublicShare | null,
    projectId: number | undefined,
    fileId: number,
): Promise<any[]> => {
    const pid = (share && share.projectId) || projectId;
    if (!pid) throw new Error("No project for this video's comments.");
    let url = getBrandProjectFileMessages(pid, fileId);
    if (share && share.orderId) url = orderShareFileMessages(share.orderId, pid, fileId);
    else if (share && share.token) url = publicFileMessages(share.base, share.token, pid, fileId);
    const { data } = await axiosInstant.get(url, { params: { versions: "all" } });
    return Array.isArray(data) ? data : (data && data.data) || [];
};
